import { notifications } from "@mantine/notifications";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { adminLogout } from "State/Admin/AuthSlice/AdminLoginSlice";
import { setAdminLoggedIn } from "State/Admin/StateEvents/AdminStateEvents";

interface AdminNavbarProps {
  toggleSidebar: () => void;
  collapsed: boolean;
  toggleCollapse: () => void;
}

const AdminNavbar: React.FC<AdminNavbarProps> = ({
  toggleSidebar,
  collapsed,
  toggleCollapse,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(adminLogout());
    dispatch(setAdminLoggedIn(false));
    navigate("/admin-login");
  };
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSearchOpen &&
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav className="font-body sticky top-0 z-50 border-b border-grey-200 relative">
      <div className="absolute inset-0 bg-white" />

      <div className="absolute inset-y-0 left-0 w-[340px] bg-white" />

      <div className="relative flex items-center justify-between h-[70px] px-6">
        <div className="flex items-center h-full">
          <button
            onClick={toggleCollapse}
            className="hidden md:flex mr-3 text-gray-500 hover:text-gray-700 transition-colors"
          >
            {collapsed ? (
              <svg
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            ) : (
              <svg
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            )}
          </button>
          <button
            onClick={toggleSidebar}
            className="mr-4 text-gray-300 hover:text-blue-400 md:hidden transition-colors"
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        <div className="hidden md:flex flex-1 justify-center">
          <button
            onClick={() => setIsSearchOpen(true)}
            className="w-[420px] max-w-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 rounded-full px-5 py-2.5 flex items-center gap-3 text-gray-400 text-sm"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400 shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Search...
            <span className="ml-auto text-xs bg-gray-200 px-2 py-0.5 rounded-md">
              ⌘K
            </span>
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <button className="flex text-gray-300 hover:text-blue-400 transition-colors">
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10.268 21a2 2 0 0 0 3.464 0" />
              <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
            </svg>
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-1 text-gray-300 hover:text-gray-200 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              {isDropdownOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform text-blue-400 hover:text-blue-500"
                >
                  <path d="m18 15-6-6-6 6" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform text-blue-400 hover:text-blue-500"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              )}
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 top-[110%] w-48 bg-white rounded-md shadow-xl border border-grey-200 overflow-hidden">
                <div className="py-1">
                  <button className="flex items-center w-full px-4 py-2 text-sm text-grey-500 hover:bg-[#6366F1] hover:text-white transition-colors">
                    Profile
                  </button>
                  <button className="flex items-center w-full px-4 py-2 text-sm text-grey-500 hover:bg-[#6366F1] hover:text-white transition-colors">
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-700 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isSearchOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-28 bg-black/30 backdrop-blur-sm">
          <div
            ref={searchRef}
            className="w-[720px] max-w-[92%] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200"
          >
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-400 shrink-0"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>

                <div className="flex-1">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search documentation, settings, products..."
                    className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
                  />
                </div>

                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="shrink-0 text-[11px] bg-gray-100 border border-gray-200 px-3 py-1 rounded-md text-gray-500 hover:bg-gray-200 transition"
                >
                  esc
                </button>
              </div>
            </div>
            <div className="p-10 text-center text-sm text-gray-400">
              No recent searches
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default AdminNavbar;
